import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/** How long a row stays marked. Matches `row-flash` in tailwind.config.js. */
const FLASH_MS = 1200;

/**
 * The one row whose due date was just edited, for as long as it flashes.
 *
 * A list ordered by due date moves a row the moment its date changes, and the
 * row lands somewhere the pointer is not. The flash is what lets you follow it,
 * rather than reading the list again to find where it went.
 *
 * One id rather than a set: it marks the row you just touched, and a second
 * edit elsewhere is a new thing to look at, not a second one to keep track of.
 */
export function useRowFlash() {
  const [flashId, setFlashId] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const frame = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      window.cancelAnimationFrame(frame.current ?? 0);
    },
    [],
  );

  const flash = useCallback((id: string) => {
    window.clearTimeout(timer.current);
    window.cancelAnimationFrame(frame.current ?? 0);
    // Cleared first and set a frame later, so the same row edited twice inside
    // the window loses its class for one paint and the animation starts over.
    // Setting the same id again would be no change at all.
    setFlashId(null);
    frame.current = window.requestAnimationFrame(() => {
      setFlashId(id);
      timer.current = window.setTimeout(() => setFlashId(null), FLASH_MS);
    });
  }, []);

  return { flashId, flash };
}

/**
 * Brings a flashing row on screen. `nearest`, so a row that is already visible
 * does not move at all -- this is only for the date that sent it off the
 * bottom of the list. Read live, for the same reason SidePanel reads it live.
 */
export function useRevealOnFlash(ref: RefObject<HTMLElement | null>, flashing: boolean) {
  useEffect(() => {
    if (!flashing) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    ref.current?.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
  }, [ref, flashing]);
}
